import { getLayer, updateLayer, updateLayerProperties } from '@/api/layers';
import { LayerPropertiesByCategory } from './LayerPropertiesByCategory';
import { LayerDetailWithProduction } from '@/types/layer';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

interface LayerDetailModalProps {
  open: boolean;
  onClose: () => void;
  layerId: string | null;
  onLayerUpdated?: () => void;
}

export const LayerDetailModal: React.FC<LayerDetailModalProps> = ({
  open,
  onClose,
  layerId,
  onLayerUpdated,
}) => {
  const [layer, setLayer] = useState<LayerDetailWithProduction | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    description: '',
    type: '',
  });
  const [propertiesData, setPropertiesData] = useState<{ [key: string]: string | number }>({});
  const [viewMode, setViewMode] = useState<'plot' | 'table'>('plot');

  useEffect(() => {
    if (open && layerId) {
      setEditMode(false); // Сбрасываем режим редактирования при открытии
      loadLayer();
    }
  }, [open, layerId]);

  const loadLayer = async () => {
    if (!layerId) return;

    setLoading(true);
    setError(null);

    try {
      const layerData = await getLayer(layerId);

      if (!layerData) {
        throw new Error('Reservoir data is undefined');
      }

      setLayer(layerData);
      setEditData({
        name: layerData.name || '',
        description: layerData.description || '',
        type: layerData.type || '',
      });

      // Initialize properties data
      const initialProperties: { [key: string]: string | number } = {};
      if (layerData.properties) {
        layerData.properties.forEach(prop => {
          // Always add the property, even if value is null/undefined
          initialProperties[prop.name] = prop.value !== null && prop.value !== undefined ? prop.value : '';
        });
      }
      setPropertiesData(initialProperties);
    } catch (err) {
      console.error('Error loading layer:', err);
      setError(err instanceof Error ? err.message : 'Failed to load layer details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!layer) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Current propertiesData:', propertiesData);

      // Update layer basic info
      await updateLayer(layer.id, {
        name: editData.name,
        description: editData.description || undefined,
        type: editData.type || undefined,
      });

      console.log('After updateLayer, propertiesData still:', propertiesData);

      // Send all form properties; empty → null so backend deletes sparse rows
      const propertiesUpdate = {
        properties: Object.entries(propertiesData).map(([name, value]) => {
          const property = layer.properties.find(p => p.name === name);
          const isEmpty = value === null || value === undefined || value === '';
          return {
            attribute_id: property?.attribute_id || '',
            value: isEmpty ? null : value,
          };
        }).filter(p => p.attribute_id)
      };

      console.log('Properties update payload:', propertiesUpdate);
      console.log('About to call updateLayerProperties with payload:', propertiesUpdate);

      await updateLayerProperties(layer.id, propertiesUpdate);

      // Reload layer data to get the latest state
      const reloadedLayer = await getLayer(layer.id);
      setLayer(reloadedLayer);

      // Update properties data with reloaded data
      const updatedProperties: { [key: string]: string | number } = {};
      if (reloadedLayer.properties) {
        reloadedLayer.properties.forEach(prop => {
          if (prop.value !== null && prop.value !== undefined) {
            updatedProperties[prop.name] = prop.value;
          }
        });
      }
      setPropertiesData(updatedProperties);

      setEditMode(false);
      console.log('Reservoir updated successfully, calling onLayerUpdated');
      onLayerUpdated?.();
    } catch (err) {
      console.error('Error updating layer:', err);
      setError(err instanceof Error ? err.message : 'Failed to update layer');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original values
    if (layer) {
      setEditData({
        name: layer.name || '',
        description: layer.description || '',
        type: layer.type || '',
      });

      // Reset properties data
      const initialProperties: { [key: string]: string | number } = {};
      if (layer.properties) {
        layer.properties.forEach(prop => {
          // Always add the property, even if value is null/undefined
          initialProperties[prop.name] = prop.value !== null && prop.value !== undefined ? prop.value : '';
        });
      }
      setPropertiesData(initialProperties);
    }
    setEditMode(false);
  };

  const handlePropertyChange = (propertyName: string, value: string | number) => {
    console.log('Property change:', propertyName, '=', value);
    setPropertiesData(prev => {
      const updated = {
        ...prev,
        [propertyName]: value,
      };
      console.log('Updated propertiesData:', updated);
      return updated;
    });
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'oil':
        return 'success';
      case 'gas':
        return 'info';
      case 'oil and gas':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatChartData = (productionData: LayerDetailWithProduction['production_data']) => {
    return productionData.map(data => ({
      year: String(data.year),
      oil: data.oil || 0,
      gas: data.gas || 0,
      liquid: (data.oil || 0) + (data.water || 0),
      waterCut: data.oil != null || data.water != null
        ? ((data.water || 0) / ((data.oil || 0) + (data.water || 0) || 1)) * 100
        : 0,
    }));
  };

  if (!layer && !loading) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {editMode ? 'Edit Reservoir' : 'Reservoir Details'}
          </Typography>
          {layer?.type && (
            <Chip
              label={layer.type}
              color={getTypeColor(layer.type) as any}
              size="small"
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && !layer ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : layer ? (
          <Box>
            {/* Reservoir Information */}
            <Typography variant="h6" gutterBottom>
              Reservoir Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={!editMode}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!editMode}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editData.type}
                    label="Type"
                    onChange={(e) => setEditData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <MenuItem value="oil">Oil</MenuItem>
                    <MenuItem value="gas">Gas</MenuItem>
                    <MenuItem value="oil and gas">Oil and Gas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editData.description}
                  onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={!editMode}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            {/* Reservoir Productivity Data */}
            {layer.production_data && layer.production_data.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Reservoir Productivity Data
                </Typography>

                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tabs
                        value={viewMode}
                        onChange={(_, newValue) => setViewMode(newValue)}
                        aria-label="data view tabs"
                      >
                        <Tab label="Plot" value="plot" />
                        <Tab label="Table" value="table" />
                      </Tabs>
                    </Box>

                    {viewMode === 'plot' ? (
                      <Box sx={{ height: 400, width: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={formatChartData(layer.production_data)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" />
                            <YAxis yAxisId="rates" />
                            <YAxis yAxisId="wc" orientation="right" domain={[0, 100]} />
                            <Tooltip
                              formatter={(value, name) => [
                                `${Number(value).toFixed(2)} ${name === 'Water cut' ? '%' : '1000 t'}`,
                                name === 'oil' ? 'Oil' : name === 'gas' ? 'Gas' : name === 'liquid' ? 'Liquid' : 'Water cut'
                              ]}
                            />
                            <Legend />
                            <Line
                              yAxisId="rates"
                              type="monotone"
                              dataKey="oil"
                              stroke="#8884d8"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              name="Oil"
                            />
                            <Line
                              yAxisId="rates"
                              type="monotone"
                              dataKey="gas"
                              stroke="#82ca9d"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              name="Gas"
                            />
                            <Line
                              yAxisId="rates"
                              type="monotone"
                              dataKey="liquid"
                              stroke="#4ecdc4"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              name="Liquid"
                            />
                            <Line
                              yAxisId="wc"
                              type="monotone"
                              dataKey="waterCut"
                              stroke="#ffc658"
                              strokeWidth={2}
                              dot={{ r: 4 }}
                              name="Water cut"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Year</TableCell>
                              <TableCell align="right">Oil (1000 t)</TableCell>
                              <TableCell align="right">Water (1000 t)</TableCell>
                              <TableCell align="right">Gas (1000 t)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {layer.production_data.map((data, index) => (
                              <TableRow key={index}>
                                <TableCell>{data.year}</TableCell>
                                <TableCell align="right">
                                  {data.oil ? data.oil.toFixed(2) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {data.water ? data.water.toFixed(2) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {data.gas ? data.gas.toFixed(2) : '-'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* Properties */}
            <Typography variant="h6" gutterBottom>
              Reservoir Properties
            </Typography>

            <Box sx={{ maxHeight: 600, overflow: 'auto', mt: 2 }}>
              <LayerPropertiesByCategory
                properties={layer.properties.map(prop => ({
                  id: prop.id,
                  name: prop.name,
                  unit: prop.unit,
                  value: prop.value,
                  value_type: prop.value_type,
                  description: prop.description || null,
                }))}
                propertiesData={propertiesData}
                onPropertyChange={handlePropertyChange}
                disabled={!editMode}
                readOnly={!editMode}
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>

        {layer && (
          <>
            {editMode ? (
              <>
                <Button onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  disabled={loading}
                >
                  Save
                </Button>
              </>
            ) : layer.can_manage ? (
              <Button
                onClick={() => setEditMode(true)}
                variant="contained"
              >
                Edit
              </Button>
            ) : null}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
